'use strict';

/* ---- Gallery ---- */
class OliGallery {
  constructor(el) {
    this.slides = [...el.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...el.querySelectorAll('[data-gallery-thumb]')];
    this.active = 0;
    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
    el.querySelector('.oli-gallery__prev')?.addEventListener('click', () => this.prev());
    el.querySelector('.oli-gallery__next')?.addEventListener('click', () => this.next());
    this._updateTabindex(0);
  }

  _updateTabindex(active) {
    this.slides.forEach((s, i) => s.setAttribute('tabindex', i === active ? '0' : '-1'));
  }

  go(i) {
    this.slides[this.active]?.classList.remove('is-active');
    this.thumbs[this.active]?.classList.remove('is-active');
    this.active = i;
    this.slides[i]?.classList.add('is-active');
    this.thumbs[i]?.classList.add('is-active');
    this._updateTabindex(i);
    const thumb = this.thumbs[i];
    if (thumb) {
      const container = thumb.parentElement;
      const thumbLeft = thumb.getBoundingClientRect().left - container.getBoundingClientRect().left + container.scrollLeft;
      const target = thumbLeft + thumb.offsetWidth / 2 - container.clientWidth / 2;
      container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }

  prev() { this.go((this.active - 1 + this.slides.length) % this.slides.length); }
  next() { this.go((this.active + 1) % this.slides.length); }
}

/* ---- Accordion ---- */
class OliAccordion {
  constructor(el) {
    this.el = el;
    this.body = el.querySelector('.oli-acc__body');
    this.btn  = el.querySelector('.oli-acc__btn');

    if (!this.body.id) {
      this.body.id = 'oli-acc-body-' + Math.random().toString(36).slice(2, 8);
    }
    this.btn.setAttribute('aria-controls', this.body.id);

    this.btn.addEventListener('click', () => this.toggle());

    if (el.hasAttribute('data-open')) {
      this._open();
    } else {
      this.btn.setAttribute('aria-expanded', 'false');
    }
  }

  _open() {
    this.el.parentElement?.querySelectorAll('.oli-acc').forEach(sib => {
      if (sib !== this.el && sib.classList.contains('oli-acc--open')) {
        sib.classList.remove('oli-acc--open');
        sib.querySelector('.oli-acc__body').style.maxHeight = '0';
        sib.querySelector('.oli-acc__btn').setAttribute('aria-expanded', 'false');
      }
    });
    this.el.classList.add('oli-acc--open');
    this.body.style.maxHeight = this.body.scrollHeight + 'px';
    this.btn.setAttribute('aria-expanded', 'true');
  }

  _close() {
    this.el.classList.remove('oli-acc--open');
    this.body.style.maxHeight = '0';
    this.btn.setAttribute('aria-expanded', 'false');
  }

  toggle() {
    this.el.classList.contains('oli-acc--open') ? this._close() : this._open();
    const headerH = document.querySelector('#shopify-section-header')?.offsetHeight || 0;
    const top = this.el.parentElement.getBoundingClientRect().top + window.scrollY - headerH - 250;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

/* ---- Product configurator ---- */
class OliConfigurator {
  constructor(panel) {
    this.panel = panel;
    this.base = parseInt(panel.dataset.basePrice || '0', 10);

    this.totalEl    = panel.querySelector('[data-total-amount]');
    this.ctaEl      = panel.querySelector('[data-add-to-cart]');
    this.barTotalEl = document.querySelector('[data-bar-total]');
    this.barCtaEl   = document.querySelector('[data-bar-cta]');

    panel.querySelectorAll('[data-radio-group]').forEach(group => {
      const name = group.dataset.radioGroup;
      const opts = [...group.querySelectorAll('.oli-opt')];
      opts.forEach((opt, idx) => {
        opt.addEventListener('click', () => this._selectRadio(name, opt, group));
        opt.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._selectRadio(name, opt, group);
          } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            opts[(idx + 1) % opts.length].focus();
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            opts[(idx - 1 + opts.length) % opts.length].focus();
          }
        });
      });
    });

    panel.querySelectorAll('[data-check-group] .oli-opt').forEach(opt => {
      opt.addEventListener('click', () => this._toggleCheck(opt));
      opt.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._toggleCheck(opt);
        }
      });
    });

    this._update();
  }

  _selectRadio(name, chosen, group) {
    group.querySelectorAll('.oli-opt').forEach(o => {
      o.classList.remove('is-on');
      o.setAttribute('aria-checked', 'false');
      o.setAttribute('tabindex', '-1');
    });
    chosen.classList.add('is-on');
    chosen.setAttribute('aria-checked', 'true');
    chosen.setAttribute('tabindex', '0');
    this._update();
  }

  _toggleCheck(opt) {
    opt.classList.toggle('is-on');
    opt.setAttribute('aria-checked', opt.classList.contains('is-on') ? 'true' : 'false');
    this._update();
  }

  _total() {
    let t = this.base + (this.easifyDelta || 0);
    this.panel.querySelectorAll('.oli-opt.is-on').forEach(opt => {
      const price = parseInt(opt.dataset.addonPrice || '0', 10);
      t += price;
    });
    return t;
  }

  _fmt(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  _update() {
    const t = this._total();
    const formatted = this._fmt(t);
    const ctaLabel = 'Add to cart - ' + formatted;
    if (this.totalEl)    this.totalEl.textContent = formatted;
    if (this.ctaEl)      this.ctaEl.textContent   = ctaLabel;
    if (this.barTotalEl) this.barTotalEl.textContent = formatted;
    if (this.barCtaEl)   this.barCtaEl.textContent   = ctaLabel;
  }

  buildItems() {
    const items = [];
    const form = this.panel.querySelector('form');
    const idInput = form && form.querySelector('input[name="id"]');
    const mainId = idInput ? parseInt(idInput.value, 10) : parseInt(this.panel.dataset.mainVariantId || '0', 10);
    if (mainId) items.push({ id: mainId, quantity: 1 });

    this.panel.querySelectorAll('.oli-opt.is-on').forEach(opt => {
      const variantId = parseInt(opt.dataset.variantId || '0', 10);
      if (!variantId) return;
      items.push({ id: variantId, quantity: 1 });
    });
    return items;
  }
}

/* ---- Lightbox ---- */
class OliLightbox {
  constructor() {
    this.el = document.getElementById('oli-lightbox');
    if (!this.el) return;

    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.setAttribute('aria-label', 'Image viewer');

    this.stage    = this.el.querySelector('.oli-lightbox__stage');
    this.items    = [...this.el.querySelectorAll('.oli-lightbox__item')];
    this.counter  = this.el.querySelector('.oli-lightbox__counter');
    this.count    = this.items.length;
    this.active   = 0;
    this._trigger = null;

    this.el.querySelector('.oli-lightbox__close')?.addEventListener('click', () => this.close());
    this.el.querySelector('.oli-lightbox__prev')?.addEventListener('click', () => this.prev());
    this.el.querySelector('.oli-lightbox__next')?.addEventListener('click', () => this.next());

    this.stage.addEventListener('click', e => { if (!e.target.closest('img')) this.close(); });

    this._onKey = e => {
      if (e.key === 'Escape')     { this.close(); return; }
      if (e.key === 'ArrowLeft')  { this.prev(); return; }
      if (e.key === 'ArrowRight') { this.next(); return; }
      if (e.key === 'Tab') {
        const focusable = [...this.el.querySelectorAll('button:not([disabled])')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
        }
      }
    };

    this.stage.addEventListener('scroll', () => this._syncCounter(), { passive: true });

    document.querySelectorAll('[data-lightbox-index]').forEach(slide => {
      slide.addEventListener('click', () => {
        this._trigger = slide;
        this.open(parseInt(slide.dataset.lightboxIndex, 10));
      });
      slide.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._trigger = slide;
          this.open(parseInt(slide.dataset.lightboxIndex, 10));
        }
      });
    });
  }

  open(i) {
    this.el.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKey);
    this._scrollTo(i, false);
    const closeBtn = this.el.querySelector('.oli-lightbox__close');
    if (closeBtn) closeBtn.focus();
  }

  close() {
    this.el.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKey);
    if (this._trigger) { this._trigger.focus(); this._trigger = null; }
  }

  prev() { this._scrollTo((this.active - 1 + this.count) % this.count); }
  next() { this._scrollTo((this.active + 1) % this.count); }

  _scrollTo(i, smooth = true) {
    this.active = i;
    this.stage.scrollTo({ left: i * this.stage.offsetWidth, behavior: smooth ? 'smooth' : 'instant' });
    this._updateCounter(i);
  }

  _syncCounter() {
    const i = Math.round(this.stage.scrollLeft / this.stage.offsetWidth);
    if (i !== this.active) { this.active = i; this._updateCounter(i); }
  }

  _updateCounter(i) {
    if (this.counter && this.count > 1) this.counter.textContent = `${i + 1} / ${this.count}`;
  }
}

/* ---- Add to cart ---- */
function oli_addToCart(items, btn) {
  if (!items.length) return;
  btn.disabled = true;

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items }),
  })
    .then(r => r.json())
    .then(() => {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      const drawerToggle = document.querySelector('[aria-controls="CartDrawer"]');
      if (drawerToggle) drawerToggle.click();
    })
    .catch(err => console.error('[Oli] cart/add error:', err))
    .finally(() => { btn.disabled = false; });
}

/* ---- Sticky buy bar ---- */
class OliBuyBar {
  constructor(el) {
    this.el  = el;
    this.btn = el.querySelector('button');
    this._setVisible(false);
    window.addEventListener('scroll', () => {
      this._setVisible(window.scrollY > 760);
    }, { passive: true });
  }

  _setVisible(visible) {
    this.el.classList.toggle('oli-buybar--show', visible);
    this.el.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (this.btn) this.btn.setAttribute('tabindex', visible ? '0' : '-1');
  }
}

/* ---- Review filter ---- */
class OliReviews {
  constructor(el) {
    this.cards = [...el.querySelectorAll('.oli-review-card')];
    el.querySelectorAll('.oli-filter-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        el.querySelectorAll('.oli-filter-chip').forEach(c => c.classList.remove('is-active'));
        chip.classList.add('is-active');
        const tag = chip.dataset.tag;
        this.cards.forEach(card => {
          const tags = (card.dataset.tags || '').split(',');
          card.hidden = tag !== 'all' && !tags.includes(tag);
        });
      });
    });
  }
}

/* ---- Init ---- */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-oli-gallery]').forEach(el => new OliGallery(el));
  new OliLightbox();
  document.querySelectorAll('.oli-acc').forEach(el => new OliAccordion(el));

  const panel = document.querySelector('[data-oli-panel]');
  if (panel) {
    const cfg = new OliConfigurator(panel);

    const form = panel.querySelector('form');

    // Bar button delegates to the real submit button so Dawn + Easify handle it natively
    const barBtn = document.querySelector('[data-bar-cta]');
    if (barBtn && form) {
      barBtn.addEventListener('click', () => {
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.click();
      });
    }

    const syncEasifyPrice = () => {
      const tpoEl = document.querySelector('input.tpo_total-additional-price-input');
      if (tpoEl) {
        const raw = tpoEl.getAttribute('value') || tpoEl.value || '0';
        const cents = Math.round(parseFloat(raw.replace(/[^0-9.]/g, '')) * 100);
        cfg.easifyDelta = isNaN(cents) ? 0 : cents;
      } else {
        const priceEl = panel.querySelector('.price-item--regular');
        if (!priceEl) return;
        const cents = Math.round(parseFloat(priceEl.textContent.trim().replace(/,/g, '').replace(/[^0-9.]/g, '')) * 100);
        if (isNaN(cents) || cents <= 0) return;
        cfg.easifyDelta = cents - cfg.base;
      }
      cfg._update();
    };

    new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.target.classList?.contains('tpo_total-additional-price-input')) {
          syncEasifyPrice();
          break;
        }
      }
    }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['value'] });

    ['easify:price:update', 'easify:price:updated', 'easify:options:change', 'product:price:update'].forEach(n => {
      document.addEventListener(n, syncEasifyPrice);
    });
  }

  const buybar = document.querySelector('.oli-buybar');
  if (buybar) new OliBuyBar(buybar);

  const reviewsEl = document.querySelector('[data-oli-reviews]');
  if (reviewsEl) new OliReviews(reviewsEl);

  if (typeof lucide !== 'undefined') lucide.createIcons({ attrs: { 'aria-hidden': 'true' } });

});
